import React from 'react';
import { useAuthContext } from '../../context/AuthContext';
import { QuotaStatus } from '../../types';
import {
  breakdownRows,
  formatTokens,
  formatExactTokens,
  formatUsagePeriod,
  hasQuotaLimit,
  usagePercent,
  usageTotal,
} from '../../utils/quota';

const QuotaPopup: React.FC<{ quota: QuotaStatus; onClose: () => void }> = ({ quota, onClose }) => {
  const { user } = useAuthContext();
  const orgId = localStorage.getItem('w3_org_id') || user?.email || '';
  const rows = breakdownRows(quota.by_category, quota.by_domain);
  const total = usageTotal(quota);
  const quotaAvailable = hasQuotaLimit(quota);
  const scopeLabel = quota.usage_scope === 'user' ? 'הצריכה שלך' : 'צריכת הארגון';

  const copyOrgId = () => {
    try {
      const p = navigator.clipboard?.writeText(String(orgId));
      if (p && typeof p.then === 'function') {
        p.catch(() => { /* permission denied — no-op */ });
      }
    } catch {
      /* clipboard unavailable — no-op */
    }
  };

  return (
    <div
      dir="rtl"
      className="fixed end-2 top-12 z-50 mt-2 w-72 max-w-[calc(100vw-1rem)] rounded-lg bg-white p-4 text-gray-800 shadow-xl ring-1 ring-black/10 sm:absolute sm:end-0 sm:top-auto"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-sm">שימוש בטוקנים</span>
        <button onClick={onClose} aria-label="סגור פירוט שימוש" className="min-h-8 min-w-8 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600">✕</button>
      </div>
      <div className="border-y border-gray-100 py-3">
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-xs text-gray-500">{scopeLabel} · {formatUsagePeriod(quota.year_month)}</p>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-950">{formatExactTokens(total)}</p>
          </div>
          <span className="mb-1 rounded-full bg-amber-50 px-2 py-1 text-[10px] font-semibold text-amber-800">הערכה</span>
        </div>
        <p className="mt-1 text-[11px] text-gray-400">טוקנים שנמדדו בפעולות המוצר במערכת</p>
      </div>
      <p className="mb-2 mt-3 text-[11px] font-semibold text-gray-500">פירוט לפי מוצר</p>
      <ul className="space-y-1 text-sm">
        {rows.map((r) => (
          <li key={r.category} className="flex items-center justify-between">
            <span>{r.label}</span>
            <span className="tabular-nums text-gray-500">{formatTokens(r.tokens)}</span>
          </li>
        ))}
      </ul>
      {quotaAvailable && (
        <div className="mt-3 border-t border-gray-200 pt-3 text-xs">
          <div className="flex items-center justify-between text-gray-600">
            <span>מכסה זמינה ב־W3</span>
            <span className="tabular-nums">{formatTokens(quota.used)} / {formatTokens(quota.limit)}</span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
            <div className="h-full bg-primary-500" style={{ width: `${usagePercent(quota.used, quota.limit)}%` }} />
          </div>
          <p className="mt-1 text-gray-400">נותרו {formatTokens(quota.remaining)} טוקנים</p>
        </div>
      )}
      <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
        <p className="mb-2">מזהה חשבון לצורכי ניהול עתידי:</p>
        <button
          onClick={copyOrgId}
          className="w-full truncate rounded bg-gray-100 hover:bg-gray-200 px-2 py-1 text-gray-700"
          title="העתק מזהה"
        >
          {orgId || '—'} ⧉
        </button>
        <p className="mt-2 leading-4 text-gray-400">הנתון מיועד לניטור. חיוב יופעל רק לאחר מעבר למדידה מאומתת.</p>
      </div>
    </div>
  );
};

export default QuotaPopup;
